import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { Package, Plus, Pencil, Trash2, Search } from 'lucide-react';
import { toast } from 'sonner';

function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    category_id: '',
    image_url: '',
    stock: '',
    supplier: 'ED STEEL'
  });

  useEffect(() => {
    loadProducts();
    loadCategories();
  }, []);

  async function loadProducts() {
    try {
      const response = await api.get('/products');
      setProducts(response.data || []);
    } catch (error) {
      toast.error('Fehler beim Laden der Produkte');
    } finally {
      setLoading(false);
    }
  }

  async function loadCategories() {
    try {
      const response = await api.get('/categories');
      setCategories(response.data || []);
    } catch (error) {
      console.error('Failed to load categories', error);
    }
  }

  function openCreateDialog() {
    setEditingProduct(null);
    setFormData({
      name: '',
      description: '',
      price: '',
      category_id: categories[0]?.id || '',
      image_url: '',
      stock: '0',
      supplier: 'ED STEEL'
    });
    setIsDialogOpen(true);
  }

  function openEditDialog(product) {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      description: product.description,
      price: product.price.toString(),
      category_id: product.category_id,
      image_url: product.image_url,
      stock: (product.stock || 0).toString(),
      supplier: product.supplier || 'ED STEEL'
    });
    setIsDialogOpen(true);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    
    const productData = {
      name: formData.name,
      description: formData.description,
      price: parseFloat(formData.price),
      category_id: formData.category_id,
      image_url: formData.image_url,
      stock: parseInt(formData.stock) || 0,
      supplier: formData.supplier
    };

    try {
      if (editingProduct) {
        await api.put(`/admin/products/${editingProduct.id}`, productData);
        setProducts(products.map(p => p.id === editingProduct.id ? {...p, ...productData} : p));
        toast.success('Produkt aktualisiert');
      } else {
        const response = await api.post('/admin/products', productData);
        setProducts([...products, response.data]);
        toast.success('Produkt erstellt');
      }
      setIsDialogOpen(false);
    } catch (error) {
      toast.error('Fehler beim Speichern');
    }
  }

  async function deleteProduct(productId) {
    if (!confirm('Produkt wirklich löschen?')) return;
    
    try {
      await api.delete(`/admin/products/${productId}`);
      setProducts(products.filter(p => p.id !== productId));
      toast.success('Produkt gelöscht');
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  }

  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || product.category_id === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  function getCategoryName(categoryId) {
    return categories.find(c => c.id === categoryId)?.name_de || categoryId;
  }

  if (loading) {
    return <div className="text-center py-8">Wird geladen...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Package className="h-6 w-6" />
          <h2 className="text-2xl font-heading font-bold">Produkte ({products.length})</h2>
        </div>
        <Button onClick={openCreateDialog} data-testid="add-product-btn">
          <Plus className="h-4 w-4 mr-2" />
          Neues Produkt
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Produkt suchen..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="product-search"
          />
        </div>
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-[200px]" data-testid="category-filter">
            <SelectValue placeholder="Kategorie filtern" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle Kategorien</SelectItem>
            {categories.map(c => (
              <SelectItem key={c.id} value={c.id}>{c.name_de}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Products Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {filteredProducts.map(product => (
          <div key={product.id} className="bg-white border rounded-lg overflow-hidden" data-testid={`admin-product-${product.id}`}>
            <div className="aspect-square bg-gray-100">
              <img 
                src={product.image_url} 
                alt={product.name}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="p-4">
              <h3 className="font-semibold truncate">{product.name}</h3>
              <p className="text-sm text-gray-500 truncate">{getCategoryName(product.category_id)}</p>
              <div className="flex justify-between items-center mt-2">
                <span className="font-bold text-lg">€{product.price.toFixed(2)}</span>
                <span className="text-sm text-gray-500">Bestand: {product.stock || 0}</span>
              </div>
              <div className="flex gap-2 mt-3">
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1"
                  onClick={() => openEditDialog(product)}
                  data-testid={`edit-product-${product.id}`}
                >
                  <Pencil className="h-4 w-4 mr-1" />
                  Bearbeiten
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-red-500 hover:text-red-700"
                  onClick={() => deleteProduct(product.id)}
                  data-testid={`delete-product-${product.id}`}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingProduct ? 'Produkt bearbeiten' : 'Neues Produkt'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                required
                data-testid="product-name-input"
              />
            </div>
            <div>
              <Label htmlFor="description">Beschreibung</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                rows={3}
                data-testid="product-description-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="price">Preis (€)</Label>
                <Input
                  id="price"
                  type="number"
                  step="0.01"
                  value={formData.price}
                  onChange={(e) => setFormData({...formData, price: e.target.value})}
                  required
                  data-testid="product-price-input"
                />
              </div>
              <div>
                <Label htmlFor="stock">Bestand</Label>
                <Input
                  id="stock"
                  type="number"
                  value={formData.stock}
                  onChange={(e) => setFormData({...formData, stock: e.target.value})}
                  data-testid="product-stock-input"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="category">Kategorie</Label>
              <Select 
                value={formData.category_id} 
                onValueChange={(value) => setFormData({...formData, category_id: value})}
              >
                <SelectTrigger data-testid="product-category-select">
                  <SelectValue placeholder="Kategorie wählen" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.name_de}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="image_url">Bild-URL</Label>
              <Input
                id="image_url"
                value={formData.image_url}
                onChange={(e) => setFormData({...formData, image_url: e.target.value})}
                placeholder="https://..."
                data-testid="product-image-input"
              />
            </div>
            <div>
              <Label htmlFor="supplier">Lieferant</Label>
              <Select 
                value={formData.supplier} 
                onValueChange={(value) => setFormData({...formData, supplier: value})}
              >
                <SelectTrigger data-testid="product-supplier-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ED STEEL">ED STEEL</SelectItem>
                  <SelectItem value="Fripac Medis">Fripac Medis</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                Abbrechen
              </Button>
              <Button type="submit" data-testid="save-product-btn">
                {editingProduct ? 'Speichern' : 'Erstellen'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default AdminProducts;
